// หน้าแตะบัตรละหมาด — ครูเวรถือมือถือยืนที่ทางเข้า-ออกมัสยิด
//
// ออกแบบให้ใช้มือเดียวกลางแจ้งตอนหัวค่ำ: ปุ่มใหญ่ ตัวเลขใหญ่ ผลการแตะเต็มจอ
// ครูจะไม่ได้มองจอตลอดเวลา เสียงกับสีจึงต้องบอกผลได้โดยไม่ต้องอ่าน
//
// นักเรียนแตะตอนไหนก็ได้ในหน้าต่างเวลา — ขาขึ้นไปละหมาด หรือหลังละหมาดเสร็จ
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import useNfcReader, { nfcUnavailableReason, isValidUid, normalizeUid } from '../utils/useNfcReader';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';
const UPLOAD_BASE = API.replace(/\/api\/?$/, '').replace(/\/$/, '');
const photoUrl = (photo) => (photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null);

const clock = (value) =>
  (value ? new Date(value).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '-');

// ผลการแตะที่ครูต้องแยกออกจากกันได้ในเสี้ยววินาที
// สีเขียว = ผ่าน, เหลือง = ต้องดูอีกที, แดง = ยังไม่ถูกบันทึก
const RESULT_STYLE = {
  ok:            { variant: 'success', icon: 'ri-checkbox-circle-fill', title: 'บันทึกแล้ว' },
  already:       { variant: 'secondary', icon: 'ri-history-line', title: 'แตะไปแล้ว' },
  // โหมดเงียบ: เครื่องจำได้แต่ยังไม่เช็คชื่อให้ ต้องไม่ใช้สีเขียวเด็ดขาด
  // ไม่งั้นครูจะอ่านว่าเช็คแล้วแล้วปล่อยเด็กไป ทั้งที่ผลยังเป็น "ขาด" อยู่
  shadow:        { variant: 'info', icon: 'ri-eye-line', title: 'จำได้ (โหมดทดสอบ — ยังไม่เช็คชื่อให้)' },
  no_dorm_face:  { variant: 'warning', icon: 'ri-home-off-line', title: 'จำได้ แต่ไม่มีที่นอนในหอ' },
  face_unavailable: { variant: 'danger', icon: 'ri-camera-off-line', title: 'ตัวจดจำใบหน้าไม่ตอบ' },
  unknown_card:  { variant: 'danger', icon: 'ri-error-warning-fill', title: 'บัตรยังไม่ได้ผูกกับใคร' },
  not_student:   { variant: 'danger', icon: 'ri-user-forbid-line', title: 'ไม่ใช่บัตรนักเรียน' },
  invalid_uid:   { variant: 'danger', icon: 'ri-signal-wifi-error-line', title: 'อ่านบัตรไม่ได้' },
  outside_window:{ variant: 'warning', icon: 'ri-time-line', title: 'ยังไม่ถึงเวลารับแตะบัตร' },
  closed:        { variant: 'warning', icon: 'ri-lock-line', title: 'รอบนี้ปิดไปแล้ว' },
  error:         { variant: 'danger', icon: 'ri-close-circle-fill', title: 'บันทึกไม่สำเร็จ' },
};

// ส่งเฟรมทุก 1.5 วินาที และห้ามส่งทับกัน (faceBusyRef) — ตัวจดจำใช้เวลาราว
// 1 วินาทีต่อเฟรมบนซีพียู ถ้าส่งถี่กว่าที่มันทำทัน คิวจะยาวขึ้นเรื่อยๆ จนผล
// ที่ขึ้นจอเป็นของเมื่อครู่ที่แล้ว ซึ่งแย่กว่าส่งช้าลง
const FACE_INTERVAL_MS = 1500;

// คนเดิมขึ้นรายการซ้ำได้ไม่ถี่กว่า 20 วินาที
// คนที่ยืนคุยอยู่หน้ากล้องจะถูกจำได้ทุกเฟรม ถ้าไม่กันไว้ รายการ 8 ช่องจะเป็น
// ชื่อเขาคนเดียวทั้งหมด แล้วครูจะมองไม่เห็นคนที่เพิ่งผ่านไปจริงๆ
const FACE_REPEAT_MS = 20000;

// เสียงสั้นๆ สร้างจาก WebAudio ไม่ต้องมีไฟล์เสียงในโปรเจค
// ครูไม่ได้มองจอตลอด เสียงคือช่องทางหลักที่บอกว่าแตะติดหรือไม่ติด
const beep = (ok) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = ok ? 880 : 220;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (ok ? 0.12 : 0.35));
    osc.start();
    osc.stop(ctx.currentTime + (ok ? 0.12 : 0.35));
    osc.onended = () => ctx.close();
  } catch {
    // เสียงไม่ใช่สิ่งที่ขาดไม่ได้ — เครื่องที่เล่นไม่ได้ก็ยังใช้จอกับการสั่นได้
  }
};

const vibrate = (ok) => {
  try { navigator.vibrate?.(ok ? 60 : [80, 60, 80]); } catch { /* ไม่รองรับก็ข้าม */ }
};

export default function PrayerScan() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];

  const [session, setSession] = useState(null);
  const [prayerLabel, setPrayerLabel] = useState('');
  const [times, setTimes] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [last, setLast] = useState(null);
  const [log, setLog] = useState([]);       // 8 รายการล่าสุด ไว้ให้ครูเหลือบดูว่าไม่ได้ข้ามใคร
  const [counts, setCounts] = useState({ eligible: 0, scanned: 0 });
  const [manualUid, setManualUid] = useState('');
  const [busy, setBusy] = useState(false);

  // ---- การเช็คชื่อด้วยใบหน้า ----------------------------------------------
  // เปิดได้เฉพาะเมื่อผู้ดูแลเปิดโหมดไว้ที่หน้าตั้งค่า (off / shadow / live)
  // ครูเวรเปิด-ปิดที่เครื่องตัวเองได้ แต่เปลี่ยนโหมดไม่ได้ — โหมดมีผลทั้งโรงเรียน
  const [face, setFace] = useState({ mode: 'off', ready: false, message: null });
  const [faceOn, setFaceOn] = useState(false);
  // เฟรมที่ตัดสินไม่ได้ไม่เข้ารายการ แต่ต้องเห็นว่ากล้องกำลังพยายามอยู่
  // ไม่งั้นครูจะไม่รู้ว่ามันทำงานอยู่หรือค้าง แล้วไปยืนรอเปล่าๆ
  const [faceIdle, setFaceIdle] = useState(null);
  const faceBusyRef = useRef(false);
  // st_id -> เวลาที่ขึ้นรายการล่าสุด กันคนเดิมท่วมรายการทุกเฟรมที่เขายืนอยู่หน้ากล้อง
  const faceSeenRef = useRef(new Map());

  // ถ่ายรูปตอนแตะบัตร — ปิดไว้เป็นค่าเริ่มต้น ครูเปิดเองเมื่อพร้อม
  // (จำค่าไว้ในเครื่อง เพราะครูคนเดิมถือมือถือเครื่องเดิมทุกวัน)
  const [camera, setCamera] = useState(() => {
    try { return localStorage.getItem('prayer-camera') === '1'; } catch { return false; }
  });
  const [cameraError, setCameraError] = useState(null);

  // ช่องทางที่ครูยืนอยู่ — ตัดสินว่าชื่อนักเรียนไปขึ้นจอไหน จำไว้ในเครื่องเหมือน
  // สวิตช์กล้อง เพราะครูคนเดิมมักยืนช่องเดิมทุกวัน และไม่มีใครอยากเลือกใหม่
  // วันละห้ารอบ. ค่านี้ไม่แตะผลการเช็คชื่อ เลือกผิดแค่ชื่อไปขึ้นจออื่น
  const [lanes, setLanes] = useState([]);
  const [lane, setLane] = useState(() => {
    try { return localStorage.getItem('prayer-lane') || ''; } catch { return ''; }
  });
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem('prayer-camera', camera ? '1' : '0'); } catch { /* โหมดส่วนตัว */ }
  }, [camera]);

  useEffect(() => {
    try { localStorage.setItem('prayer-lane', lane); } catch { /* โหมดส่วนตัว */ }
  }, [lane]);

  // รายชื่อช่องมาจากทะเบียนอุปกรณ์ — ถ้ายังไม่มีใครผูกช่องให้จอไหนเลย
  // ตัวเลือกจะว่าง และหน้าไม่ต้องขึ้นอะไร (โรงเรียนที่มีจอเดียวไม่ต้องเลือก)
  useEffect(() => {
    axiosInstance.get(`${API}/prayer/lanes`)
      .then(({ data }) => setLanes(Array.isArray(data) ? data : []))
      .catch(() => setLanes([]));
  }, []);

  // กล้องหลัง ไม่ใช่กล้องหน้า — เสาอากาศ NFC อยู่ด้านหลังเครื่อง นักเรียนจึงยืน
  // อยู่ฝั่งตรงข้ามจอตอนเอาบัตรมาแตะ กล้องหน้าจะได้หน้าครูทุกใบ
  useEffect(() => {
    if (!camera) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => { /* บางเบราว์เซอร์เล่นเองอยู่แล้ว */ });
        }
        setCameraError(null);
      } catch (error) {
        setCameraError(
          error?.name === 'NotAllowedError'
            ? 'ไม่ได้รับอนุญาตให้ใช้กล้อง — กดไอคอนกุญแจข้างช่องที่อยู่เว็บแล้วอนุญาตกล้อง'
            : 'เปิดกล้องไม่ได้ — เครื่องนี้อาจไม่มีกล้อง หรือหน้าเว็บไม่ได้เปิดผ่าน https'
        );
        setCamera(false);
      }
    })();

    return () => { cancelled = true; };
  }, [camera]);

  // ปิดกล้องตอนออกจากหน้า ไม่งั้นไฟกล้องค้างและแบตหมดเร็ว
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  // คืน data URL ของเฟรมปัจจุบัน หรือ null เมื่อยังไม่พร้อม
  // ย่อที่ฝั่งนี้เลย ไม่ส่งภาพเต็มความละเอียดขึ้นไป — วันละ 15,000 รูป
  // ถ้าใบละ 2MB คือ 30GB ต่อวัน ซึ่งไม่มีดิสก์ไหนรับไหว
  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!camera || !streamRef.current || !video || !video.videoWidth) return null;
    try {
      const width = 640;
      const height = Math.round((video.videoHeight / video.videoWidth) * width) || 480;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(video, 0, 0, width, height);
      return canvas.toDataURL('image/jpeg', 0.7);
    } catch {
      return null;   // ถ่ายไม่ได้ไม่ใช่เหตุให้เช็คชื่อไม่ได้
    }
  }, [camera]);

  const nfcBlockedReason = nfcUnavailableReason();

  // ยอดเข้าเกณฑ์กับยอดแตะแล้วมาพร้อมกับรอบเลย ไม่ต้องยิงอีกเส้น
  const loadSession = useCallback(async (silent) => {
    if (!silent) setLoadingSession(true);
    try {
      const { data } = await axiosInstance.get(`${API}/prayer/session/current`);
      setSession(data.session);
      setPrayerLabel(data.prayer_label || '');
      setTimes(data.times || null);
      if (data.session) setCounts({ eligible: data.eligible, scanned: data.scanned });
    } catch {
      setSession(null);
    } finally {
      setLoadingSession(false);
    }
  }, []);

  useEffect(() => { loadSession(); }, [loadSession]);

  // รอบเปลี่ยนเองตามเวลาละหมาด ครูไม่ต้องกดอะไร — เช็คทุก 1 นาที
  useEffect(() => {
    const timer = setInterval(() => loadSession(true), 60000);
    return () => clearInterval(timer);
  }, [loadSession]);

  // โหมดใบหน้าที่ผู้ดูแลตั้งไว้ + บริการพร้อมหรือยัง
  // อ่านซ้ำทุกนาที เพราะผู้ดูแลอาจปิดโหมดกลางรอบ (เช่นพบว่าทายผิดคน)
  // แล้วเครื่องของครูเวรต้องหยุดส่งเฟรมเองโดยไม่ต้องมีใครไปบอก
  const loadFace = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`${API}/prayer/face/health`);
      setFace(data);
      if (data.mode === 'off' || !data.ready) setFaceOn(false);
    } catch {
      setFace({ mode: 'off', ready: false, message: 'อ่านสถานะตัวจดจำใบหน้าไม่ได้' });
      setFaceOn(false);
    }
  }, []);

  useEffect(() => { loadFace(); }, [loadFace]);
  useEffect(() => {
    const timer = setInterval(loadFace, 60000);
    return () => clearInterval(timer);
  }, [loadFace]);

  // ส่งเฟรมเข้าไปให้ตัวจดจำใบหน้าเรื่อยๆ ระหว่างที่เปิดไว้
  //
  // ทุกอย่างที่เกิดขึ้นที่นี่เป็น "ทางเสริม" ของการแตะบัตร — ล้มเหลวได้โดยไม่
  // กระทบอะไร ครูยังแตะบัตรได้ระหว่างที่โหมดนี้ทำงานอยู่ และคนที่เครื่อง
  // ไม่กล้าทายก็เดินไปแตะบัตรตามปกติ (ดูหัวข้อ "สามผลลัพธ์" ใน face/README.md)
  useEffect(() => {
    if (!faceOn || !camera || !session) return undefined;

    let stopped = false;
    const tick = async () => {
      if (stopped || faceBusyRef.current) return;
      const image = capture();
      if (!image) return;

      faceBusyRef.current = true;
      try {
        const { data } = await axiosInstance.post(`${API}/prayer/scan/face`, {
          image,
          device: 'phone-face',
          ...(lane ? { lane } : {}),
        });
        if (stopped) return;

        const now = Date.now();
        const fresh = [];
        let undecided = 0;
        let lastReason = null;
        let nearest = null;

        for (const f of data.faces || []) {
          if (f.decision === 'undecided') {
            undecided += 1;
            lastReason = f.reason;
            nearest = f.nearest || nearest;
            continue;
          }
          const stId = f.student?.st_id;
          // คนเดิมที่เพิ่งขึ้นไปไม่นาน ไม่ต้องขึ้นซ้ำ (ดู FACE_REPEAT_MS)
          if (stId && now - (faceSeenRef.current.get(stId) || 0) < FACE_REPEAT_MS) continue;
          if (stId) faceSeenRef.current.set(stId, now);

          const result = f.decision === 'checked_in' ? 'ok'
            : f.decision === 'already' ? 'already'
              : f.decision === 'shadow_only' ? 'shadow'
                : 'no_dorm_face';

          fresh.push({
            result,
            via: 'face',
            score: f.score,
            margin: f.margin,
            student: f.student,
            checked_at: f.checked_at || null,
            at: now,
          });
        }

        setFaceIdle(
          undecided > 0
            ? { count: undecided, reason: lastReason, nearest, at: now }
            : null
        );

        if (fresh.length > 0) {
          setLast(fresh[0]);
          setLog((prev) => [...fresh, ...prev].slice(0, 8));
          // เสียงดังเฉพาะตอนเช็คชื่อให้จริง — โหมดเงียบไม่ได้เช็คให้ใคร
          // ถ้าดังเหมือนกันครูจะแยกไม่ออกว่าเด็กถูกเช็คแล้วหรือยัง
          const checked = fresh.filter((x) => x.result === 'ok').length;
          if (checked > 0) {
            beep(true);
            vibrate(true);
            setCounts((c) => ({ ...c, scanned: c.scanned + checked }));
          }
        }
      } catch (error) {
        if (stopped) return;
        const payload = error?.response?.data || {};
        // บริการล่ม/รอบปิด = หยุดส่งเฟรม แล้วบอกให้กลับไปใช้บัตร
        // การยิงต่อทุก 1.5 วินาทีใส่บริการที่ล่มอยู่ไม่ได้ช่วยอะไร
        if (payload.result === 'face_unavailable' || payload.result === 'not_configured') {
          setFaceOn(false);
          setLast({
            result: 'face_unavailable',
            message: `${payload.message || 'ตัวจดจำใบหน้าไม่ตอบ'} — ${payload.fallback || 'ให้นักเรียนแตะบัตรตามปกติ'}`,
            at: Date.now(),
          });
          beep(false);
          vibrate(false);
        } else if (payload.result === 'face_off') {
          setFaceOn(false);
          loadFace();
        } else if (payload.result === 'outside_window' || payload.result === 'closed') {
          setFaceOn(false);
          loadSession(true);
        }
      } finally {
        faceBusyRef.current = false;
      }
    };

    const timer = setInterval(tick, FACE_INTERVAL_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [faceOn, camera, session, capture, loadFace, loadSession, lane]);

  // ปิดโหมดใบหน้าเมื่อกล้องถูกปิด — ไม่มีเฟรมก็ไม่มีอะไรให้ส่ง
  useEffect(() => { if (!camera) setFaceOn(false); }, [camera]);

  const submitUid = useCallback(async (uid) => {
    setBusy(true);
    try {
      const photo = capture();
      const { data } = await axiosInstance.post(`${API}/prayer/scan`, {
        uid,
        device: 'phone',
        ...(lane ? { lane } : {}),
        ...(photo ? { photo } : {}),
      });
      const entry = { ...data, at: Date.now() };
      setLast(entry);
      setLog((prev) => [entry, ...prev].slice(0, 8));
      beep(data.result === 'ok');
      vibrate(data.result === 'ok');
      if (data.result === 'ok') setCounts((c) => ({ ...c, scanned: c.scanned + 1 }));
      if (!session || session.ps_id !== data.session_id) loadSession(true);
    } catch (error) {
      const payload = error?.response?.data || {};
      const entry = {
        result: payload.result || 'error',
        message: payload.message || 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้',
        at: Date.now(),
      };
      setLast(entry);
      setLog((prev) => [entry, ...prev].slice(0, 8));
      beep(false);
      vibrate(false);
      if (entry.result === 'outside_window' || entry.result === 'closed') loadSession(true);
    } finally {
      setBusy(false);
    }
  }, [session, loadSession, capture, lane]);

  const onNfcScan = useCallback((uid, { valid }) => {
    if (!valid) {
      setLast({ result: 'invalid_uid', message: 'อ่านบัตรได้ไม่ครบ ลองแตะใหม่ให้แนบนานขึ้น', at: Date.now() });
      beep(false);
      vibrate(false);
      return;
    }
    submitUid(uid);
  }, [submitUid]);

  const { scanning, error: nfcError, start, stop, setError } = useNfcReader(onNfcScan);

  const submitManual = (e) => {
    e.preventDefault();
    const uid = normalizeUid(manualUid);
    if (!isValidUid(uid)) {
      setError('รหัสบัตรต้องเป็นเลขฐานสิบหก 8, 14 หรือ 20 หลัก');
      return;
    }
    setError(null);
    setManualUid('');
    submitUid(uid);
  };

  const style = RESULT_STYLE[last?.result] || RESULT_STYLE.error;

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h4 className="main-title mb-0">แตะบัตรละหมาด</h4>
            <div className="fs-xs text-secondary">แตะได้ทั้งก่อนและหลังละหมาด</div>
          </div>
          <Link to="/prayer/report" className="btn btn-outline-primary">
            <i className="ri-line-chart-line me-1"></i>สถิติ
          </Link>
        </div>

        {/* สถานะรอบ — ไม่มีให้เลือกอะไร ระบบรู้เองว่าตอนนี้เป็นเวลาละหมาดไหน */}
        <Card className="card-one mb-3">
          <Card.Body className="p-2 p-md-3">
            {loadingSession ? (
              <span className="text-secondary"><Spinner size="sm" animation="border" className="me-2" />กำลังตรวจเวลาละหมาด…</span>
            ) : session ? (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Badge bg="primary" className="fs-sm">{prayerLabel}</Badge>
                <span className="fs-sm text-secondary">รับแตะบัตรถึง {clock(session.ps_closes_at)}</span>
                {session.ps_status !== 'open' && <Badge bg="warning">รอบนี้ปิดแล้ว</Badge>}
              </div>
            ) : (
              <span className="text-secondary fs-sm">
                ยังไม่ถึงเวลารับแตะบัตร
                {times && ` — เวลาละหมาดวันนี้ ${times.pt_fajr?.slice(0, 5)} / ${times.pt_dhuhr?.slice(0, 5)} / ${times.pt_asr?.slice(0, 5)} / ${times.pt_maghrib?.slice(0, 5)} / ${times.pt_isha?.slice(0, 5)}`}
              </span>
            )}
          </Card.Body>
        </Card>

        {/* ตัวเลขที่ครูต้องเห็นตลอด — ไม่มีช่อง "เหลือ" เพราะไม่มีการปิดรอบด้วยมือแล้ว
            คนที่ยังไม่แตะจะถูกระบบเหมาเป็นขาดเองเมื่อหมดเวลา */}
        {session && (
          <Row className="g-2 mb-3">
            {[
              { label: 'แตะแล้ว', value: counts.scanned, variant: 'success' },
              { label: 'เข้าเกณฑ์', value: counts.eligible, variant: 'secondary' },
            ].map((box) => (
              <Col xs={6} key={box.label}>
                <Card className="card-one h-100">
                  <Card.Body className="p-2 text-center">
                    <div className={`fs-1 fw-bold lh-1 text-${box.variant}`}>{box.value}</div>
                    <div className="fs-xs text-secondary">{box.label}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* ผลการแตะครั้งล่าสุด — เต็มความกว้าง อ่านได้จากระยะแขน */}
        <Card className={`card-one mb-3 border border-2 border-${style.variant}`}>
          <Card.Body className="p-3">
            {last ? (
              <div className="d-flex align-items-center gap-3">
                {last.student?.st_photo ? (
                  <img
                    src={photoUrl(last.student.st_photo)}
                    alt={last.student.st_name}
                    width={72}
                    height={72}
                    className="rounded-circle"
                    style={{ objectFit: 'cover' }}
                  />
                ) : (
                  <i className={`${style.icon} text-${style.variant}`} style={{ fontSize: 56, lineHeight: 1 }}></i>
                )}
                <div className="flex-fill">
                  <div className={`fw-bold fs-5 text-${style.variant}`}>{style.title}</div>
                  {last.student && (
                    <div className="fs-5">{last.student.st_name} <span className="text-secondary fs-sm">({last.student.st_idnumber})</span></div>
                  )}
                  {last.message && <div className="fs-sm text-secondary">{last.message}</div>}
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {/* มาจากใบหน้าหรือจากบัตร ต้องแยกออกได้เสมอ — เวลามีข้อโต้แย้ง
                        ว่า "ผมไม่ได้มา" คำตอบต่างกันคนละเรื่องระหว่างสองทางนี้ */}
                    {last.via === 'face' && (
                      <Badge bg="dark">
                        <i className="ri-user-search-line me-1"></i>จากใบหน้า
                        {typeof last.score === 'number' && ` ${last.score.toFixed(2)}`}
                      </Badge>
                    )}
                    {last.flag === 'burst' && <Badge bg="warning">แตะติดกันหลายใบ — ดูให้แน่ว่าเจ้าของบัตรอยู่จริง</Badge>}
                    {last.no_dorm && <Badge bg="secondary">ไม่มีที่นอนในหอ — ไม่นับในรายชื่อ</Badge>}
                    {last.photo && <Badge bg="info"><i className="ri-camera-line me-1"></i>เก็บรูปแล้ว</Badge>}
                    {last.photo_error && <Badge bg="secondary">ไม่ได้เก็บรูป: {last.photo_error}</Badge>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-secondary py-3">
                <i className="ri-rfid-line d-block mb-2" style={{ fontSize: 48 }}></i>
                ยังไม่มีการแตะบัตร
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ปุ่มเริ่มสแกน — Web NFC ต้องเริ่มจากการกดของผู้ใช้เท่านั้น เรียกอัตโนมัติไม่ได้ */}
        {nfcBlockedReason ? (
          <Alert variant="warning" className="mb-3">
            <div className="fw-semibold mb-1">เครื่องนี้แตะบัตรเองไม่ได้</div>
            <div className="fs-sm">{nfcBlockedReason} — กรอกรหัสบัตรด้านล่างแทนได้</div>
          </Alert>
        ) : (
          <div className="d-grid mb-3">
            <Button
              size="lg"
              variant={scanning ? 'outline-danger' : 'success'}
              onClick={scanning ? stop : start}
              disabled={busy}
              style={{ minHeight: 72, fontSize: '1.25rem' }}
            >
              {busy ? (
                <><Spinner size="sm" animation="border" className="me-2" />กำลังบันทึก…</>
              ) : scanning ? (
                <><i className="ri-stop-circle-line me-2"></i>หยุดสแกน</>
              ) : (
                <><i className="ri-rfid-line me-2"></i>เริ่มสแกนบัตร</>
              )}
            </Button>
          </div>
        )}

        {nfcError && <Alert variant="danger" className="fs-sm">{nfcError}</Alert>}

        {/* ช่องทางที่ครูยืนอยู่ — ขึ้นเฉพาะเมื่อมีจอที่ผูกช่องไว้จริง
            โรงเรียนที่มีจอเดียวไม่ต้องเห็นตัวเลือกที่ไม่มีความหมายกับตัวเอง */}
        {lanes.length > 0 && (
          <Card className="card-one mb-3">
            <Card.Body className="p-2 p-md-3 d-flex align-items-center gap-3">
              <div className="flex-fill">
                <div className="fw-semibold">ช่องที่ยืนอยู่</div>
                <div className="fs-xs text-secondary">
                  ตัดสินว่าชื่อนักเรียนไปขึ้นจอไหน — เลือกผิดแค่ชื่อไปขึ้นจออื่น
                  ไม่มีผลกับการเช็คชื่อ. เครื่องจำค่านี้ไว้ ไม่ต้องเลือกใหม่ทุกรอบ
                </div>
              </div>
              <Form.Select
                value={lane}
                onChange={(e) => setLane(e.target.value)}
                style={{ maxWidth: 190 }}
              >
                <option value="">ไม่ระบุ (จอรวม)</option>
                {lanes.map((l) => (
                  <option key={l.lane} value={l.lane}>
                    {l.lane}{Number(l.displays) > 0 ? '' : ' (ยังไม่มีจอ)'}
                  </option>
                ))}
              </Form.Select>
            </Card.Body>
          </Card>
        )}

        {/* ถ่ายรูปตอนแตะบัตร — ทางเสริม ไม่ใช่เงื่อนไขของการเช็คชื่อ
            ถ่ายไม่ได้ก็ยังแตะบัตรได้ตามปกติ ระบบจะบอกไว้ที่ผลการแตะว่าไม่ได้เก็บรูป */}
        <Card className="card-one mb-3">
          <Card.Body className="p-2 p-md-3">
            <div className="d-flex align-items-start gap-3">
              <div className="flex-fill">
                <Form.Check
                  type="switch"
                  id="prayer-camera-toggle"
                  checked={camera}
                  onChange={(e) => setCamera(e.target.checked)}
                  label={<span className="fw-semibold">ถ่ายรูปตอนแตะบัตร</span>}
                />
                <div className="fs-xs text-secondary mt-1">
                  เก็บไว้ตรวจย้อนหลังเวลาสงสัยว่าฝากเพื่อนแตะ และใช้เป็นข้อมูลตั้งต้นของระบบจดจำใบหน้า
                  {' '}— ใช้กล้องหลัง หันด้านหลังเครื่องไปทางนักเรียนตอนเขาแตะบัตร
                </div>
                {cameraError && <div className="fs-xs text-danger mt-1">{cameraError}</div>}

                {/* เช็คชื่อด้วยใบหน้า — ขึ้นเฉพาะเมื่อผู้ดูแลเปิดโหมดไว้แล้ว
                    ครูเวรเปิด-ปิดที่เครื่องตัวเองได้ แต่เปลี่ยนโหมดไม่ได้
                    เพราะโหมดมีผลกับทุกเครื่องพร้อมกัน */}
                {face.mode !== 'off' && (
                  <div className="border-top mt-2 pt-2">
                    <Form.Check
                      type="switch"
                      id="prayer-face-toggle"
                      checked={faceOn}
                      disabled={!camera || !face.ready || !session}
                      onChange={(e) => setFaceOn(e.target.checked)}
                      label={
                        <span className="fw-semibold">
                          เช็คชื่อด้วยใบหน้า{' '}
                          {face.mode === 'shadow'
                            ? <Badge bg="info">โหมดทดสอบ</Badge>
                            : <Badge bg="success">ใช้งานจริง</Badge>}
                        </span>
                      }
                    />
                    <div className="fs-xs text-secondary mt-1">
                      {face.mode === 'shadow'
                        ? 'เครื่องจะทายว่าเห็นใครแล้วจดไว้เทียบกับบัตร แต่ยังไม่เช็คชื่อให้ใคร — นักเรียนต้องแตะบัตรตามปกติ'
                        : 'หันกล้องไปทางนักเรียนที่เดินออกจากแถว คนที่เครื่องไม่กล้าทายให้ไปแตะบัตรตามปกติ'}
                    </div>
                    {face.ready ? (
                      <div className="fs-xs text-secondary">
                        คลัง {Number(face.identities || 0).toLocaleString('th-TH')} คน
                        {' · '}เกณฑ์ {face.threshold} (นำที่สอง {face.margin})
                        {' · '}โมเดล {face.model}
                      </div>
                    ) : (
                      <div className="fs-xs text-danger">{face.message || 'ตัวจดจำใบหน้ายังไม่พร้อม'}</div>
                    )}
                    {!camera && face.ready && (
                      <div className="fs-xs text-warning">ต้องเปิดกล้องด้านบนก่อน</div>
                    )}
                    {faceOn && faceIdle && (
                      <div className="fs-xs text-secondary mt-1">
                        <Spinner animation="grow" size="sm" className="me-1" />
                        เห็น {faceIdle.count} หน้าที่ยังไม่กล้าทาย ({faceIdle.reason})
                        {faceIdle.nearest && <> — ใกล้เคียง {faceIdle.nearest.st_name}</>}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* ต้องอยู่ใน DOM เสมอ ไม่งั้นตอนกล้องเปิดจะไม่มีที่ให้ภาพไปลง */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                width={96}
                height={72}
                className="rounded border"
                style={{ objectFit: 'cover', display: camera ? 'block' : 'none', background: '#000' }}
              />
            </div>
          </Card.Body>
        </Card>

        {/* กรอกมือ — บัตรที่มือถืออ่านไม่ติด หรือใช้เครื่องอ่านต่อคอมพิวเตอร์ */}
        <Card className="card-one mb-3">
          <Card.Body className="p-2 p-md-3">
            <Form onSubmit={submitManual}>
              <Form.Label className="fs-xs text-secondary mb-1">กรอกรหัสบัตรเอง</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  value={manualUid}
                  onChange={(e) => setManualUid(e.target.value)}
                  placeholder="เช่น 041A2B3C"
                  autoComplete="off"
                />
                <Button type="submit" variant="outline-primary" disabled={busy}>บันทึก</Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {log.length > 0 && (
          <Card className="card-one mb-3">
            <Card.Header><Card.Title as="h6">แตะล่าสุด</Card.Title></Card.Header>
            <Card.Body className="p-0">
              <ul className="list-group list-group-flush">
                {log.map((row) => {
                  const s = RESULT_STYLE[row.result] || RESULT_STYLE.error;
                  return (
                    <li key={row.at} className="list-group-item d-flex align-items-center gap-2 py-2">
                      <i className={`${s.icon} text-${s.variant}`}></i>
                      <span className="flex-fill text-truncate">
                        {row.student ? row.student.st_name : s.title}
                      </span>
                      {row.flag === 'burst' && <Badge bg="warning">แตะรัว</Badge>}
                      <span className="fs-xs text-secondary">
                        {new Date(row.at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>
    </>
  );
}
